import type { CompetitorTabType } from "@/bizminer/App/PillarInsights/Swot/_components/SwotTabs.tsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { cva, type VariantProps } from "class-variance-authority";

const swotDotVariants = cva("w-2 h-2 rounded-full", {
  variants: {
    variant: {
      strength: "bg-emerald-500",
      weakness: "bg-red-500",
      opportunity: "bg-blue-500",
      threat: "bg-orange-500",
    },
  },
});

interface SwotCardProps extends VariantProps<typeof swotDotVariants> {
  title: string;
  content: string;
}

function SwotCard({ title, content, variant }: SwotCardProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center space-x-2">
          <div className={swotDotVariants({ variant })} />
          <CardTitle className="text-card-foreground uppercase">
            {title}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{content}</p>
      </CardContent>
    </Card>
  );
}

export function CompetitorAnalysis({
  competitor,
  accountName,
}: {
  competitor: CompetitorTabType["competitor"];
  accountName: string;
}) {
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-foreground">
        {accountName} vs. {competitor.name}
      </h3>

      <div className="grid gap-6 md:grid-cols-2">
        <SwotCard
          title="Strengths"
          content={competitor.strengths}
          variant="strength"
        />
        <SwotCard
          title="Weaknesses"
          content={competitor.weaknesses}
          variant="weakness"
        />
        <SwotCard
          title="Opportunities"
          content={competitor.opportunities}
          variant="opportunity"
        />
        <SwotCard
          title="Threats"
          content={competitor.threats}
          variant="threat"
        />
      </div>
    </div>
  );
}
